import { meterAudio } from '../audio-level'

// Each bar is how loud the mic got over one step, and the bars move outward
// one step at a time.
const stepMs = 70
const barWidth = 4
const barGap = 4
// The quietest bars still show as a dot, and the loudest fill the height.
const minHeight = barWidth

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: block;
      color: var(--color-accent-strong);
      opacity: 0.3;
      /* Bars fade away toward both ends. */
      mask-image: linear-gradient(to right, transparent, black 15%, black 85%, transparent);
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
  </style>
  <canvas></canvas>
`

/**
 * How loud a `stream` is, drawn as bars in the current color that start in
 * the middle and flow out to both ends, one side mirroring the other. It only
 * listens while it has a stream, and starts afresh with each one.
 */
export class MicWaveform extends HTMLElement {
  #root: ShadowRoot
  #canvas: HTMLCanvasElement
  #stream?: MediaStream
  #stopMeter?: () => void
  #frame = 0
  // How loud each finished step was, oldest first, and the loudest so far in
  // the step under way, which started at `#stepStart`.
  #bars: number[] = []
  #current = 0
  #stepStart = 0

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    this.#canvas = this.#root.querySelector('canvas')!
  }

  disconnectedCallback() {
    this.stream = undefined
  }

  get stream() {
    return this.#stream
  }

  set stream(stream: MediaStream | undefined) {
    this.#stopMeter?.()
    this.#stopMeter = undefined
    cancelAnimationFrame(this.#frame)
    this.#stream = stream
    this.#bars = []
    this.#current = 0
    if (!stream) return

    this.#stepStart = performance.now()
    this.#stopMeter = meterAudio(stream, level => {
      this.#current = Math.max(this.#current, level)
    })
    this.#frame = requestAnimationFrame(this.#draw)
  }

  #draw = (now: number) => {
    this.#frame = requestAnimationFrame(this.#draw)
    const canvas = this.#canvas
    const scale = devicePixelRatio
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    if (canvas.width !== width * scale || canvas.height !== height * scale) {
      canvas.width = width * scale
      canvas.height = height * scale
    }

    const step = barWidth + barGap
    const center = width / 2
    const maxBars = Math.ceil(center / step) + 1
    while (now - this.#stepStart >= stepMs) {
      this.#bars.push(this.#current)
      this.#current = 0
      this.#stepStart += stepMs
    }
    if (this.#bars.length > maxBars) this.#bars.splice(0, this.#bars.length - maxBars)

    const ctx = canvas.getContext('2d')!
    ctx.setTransform(scale, 0, 0, scale, 0, 0)
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = getComputedStyle(this).color

    // The newest bars sit either side of the middle, sliding outward as
    // their step goes by.
    const slide = (now - this.#stepStart) / stepMs * step
    ctx.beginPath()
    for (let i = 0; i < this.#bars.length; i++) {
      const age = this.#bars.length - 1 - i
      const offset = barGap / 2 + age * step + slide
      const h = Math.max(minHeight, this.#bars[i]! * height)
      const y = (height - h) / 2
      ctx.roundRect(center + offset, y, barWidth, h, barWidth / 2)
      ctx.roundRect(center - offset - barWidth, y, barWidth, h, barWidth / 2)
    }
    ctx.fill()
  }
}

customElements.define('meat-mic-waveform', MicWaveform)

declare global {
  interface HTMLElementTagNameMap {
    'meat-mic-waveform': MicWaveform
  }
}
